import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { LineChart } from 'lucide-react'
import { chartPush } from '../chartPush'

/**
 * Fly-to-composer feedback for the Workbench "push chart" action. Listens for
 * chartPush.onStage and launches a ghost chip from the push button
 * (#chart-push-btn) to the composer chip (#chat-attach-chip), falling back to
 * the Ask AI pill (#ask-ai-pill) or a bottom-right guess when the chat UI is
 * not visible. Mounted once in BotDetail.
 */

interface Ghost {
  id: number
  x: number
  y: number
  dx: number
  dy: number
}

// Module-scope counter: ghost keys must be unique without reaching for
// Date.now()/Math.random() in render scope (react-hooks/purity).
let ghostSeq = 0

const FLY_TIMING: KeyframeAnimationOptions = {
  duration: 560,
  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  fill: 'forwards',
}

function GhostChip({ ghost, onDone }: { ghost: Ghost; onDone: (id: number) => void }) {
  // Stable ref callback (ghost + onDone are referentially stable) so React
  // does not re-invoke it — and restart the animation — on sibling updates.
  const animateRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return
      const reduceMotion =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      // jsdom has no Web Animations API — guard node.animate existence.
      if (reduceMotion || typeof node.animate !== 'function') {
        // Deferred so we never set state synchronously during React's commit.
        setTimeout(() => onDone(ghost.id), 0)
        return
      }
      const animation = node.animate(
        [
          { transform: 'translate(0px, 0px) scale(1)', opacity: 0.95 },
          {
            transform: `translate(${ghost.dx * 0.5}px, ${ghost.dy * 0.5 - 50}px) scale(0.96)`,
            opacity: 1,
            offset: 0.55,
          },
          { transform: `translate(${ghost.dx}px, ${ghost.dy}px) scale(0.5)`, opacity: 0.1 },
        ],
        FLY_TIMING,
      )
      animation.onfinish = () => onDone(ghost.id)
    },
    [ghost, onDone],
  )

  return (
    <div
      ref={animateRef}
      className="ai-capture-chip pointer-events-none fixed z-[100] flex items-center gap-1.5 rounded-md px-2 py-1 text-label"
      style={{ left: ghost.x, top: ghost.y }}
    >
      <LineChart size={11} className="text-primary" />
      <span className="text-foreground">Chart</span>
    </div>
  )
}

export default function ChartFlyChip() {
  const [ghosts, setGhosts] = useState<Ghost[]>([])

  useEffect(() => {
    const timers = new Set<ReturnType<typeof setTimeout>>()
    const unsubscribe = chartPush.onStage(() => {
      // Defer a beat so the composer chip (#chat-attach-chip) has rendered
      // before we measure the flight target.
      const timer = setTimeout(() => {
        timers.delete(timer)
        const btn = document.getElementById('chart-push-btn')
        if (!btn) return
        const from = btn.getBoundingClientRect()
        const fromX = from.left + from.width / 2
        const fromY = from.top + from.height / 2

        let toX: number
        let toY: number
        const chip = document.getElementById('chat-attach-chip')
        const pill = document.getElementById('ask-ai-pill')
        if (chip) {
          const to = chip.getBoundingClientRect()
          toX = to.left + 40
          toY = to.top + 14
        } else if (pill) {
          const to = pill.getBoundingClientRect()
          toX = to.left + to.width / 2
          toY = to.top + to.height / 2
        } else {
          toX = window.innerWidth - 120
          toY = window.innerHeight - 160
        }

        ghostSeq += 1
        setGhosts((prev) => [
          ...prev,
          { id: ghostSeq, x: fromX, y: fromY, dx: toX - fromX, dy: toY - fromY },
        ])
      }, 30)
      timers.add(timer)
    })
    return () => {
      unsubscribe()
      timers.forEach((t) => clearTimeout(t))
    }
  }, [])

  const removeGhost = useCallback((id: number) => {
    setGhosts((prev) => prev.filter((g) => g.id !== id))
  }, [])

  if (ghosts.length === 0) return null

  // Portal to <body>, outside #root — the .v1 scope must be re-applied so the
  // design tokens (--muted, --ai-edge-*) resolve on the ghosts. tokens.css
  // paints .v1 with var(--background); keep this wrapper transparent.
  return createPortal(
    <div className="v1" style={{ backgroundColor: 'transparent' }}>
      {ghosts.map((g) => (
        <GhostChip key={g.id} ghost={g} onDone={removeGhost} />
      ))}
    </div>,
    document.body,
  )
}
